import { useEffect, useState, useRef } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { PaperPlaneTilt, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";

export function EmailTestPanel({ onFinished }) {
  const [catalogue, setCatalogue] = useState(null);
  const [to, setTo] = useState("");
  const [keys, setKeys] = useState(new Set());
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    API.get("/platform/email-health/samples").then(({ data }) => {
      setCatalogue(data);
      setTo(data.default_to || "");
      setKeys(new Set(data.samples.map((s) => s.key)));
    }).catch(() => {});
    return () => clearInterval(timer.current);
  }, []);

  const toggle = (k) => setKeys((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleGroup = (g, on) => setKeys((prev) => { const n = new Set(prev); catalogue.samples.filter((s) => s.group === g).forEach((s) => (on ? n.add(s.key) : n.delete(s.key))); return n; });

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await API.post("/platform/email-health/test", { to, keys: [...keys] });
      setRun(data);
      toast.success(`Sending ${data.results.length} sample e-mail${data.results.length === 1 ? "" : "s"} to ${data.to}…`);
      let misses = 0;
      timer.current = setInterval(async () => {
        try {
          const { data: r } = await API.get(`/platform/email-health/test/${data.id}`);
          misses = 0;
          setRun(r);
          if (r.status !== "running") {
            clearInterval(timer.current);
            setBusy(false);
            onFinished?.();
            if (r.failed) toast.error(`${r.failed} of ${r.results.length} sample e-mails failed — see the results`);
            else toast.success(`All ${r.results.length} sample e-mails were accepted by the mail server — check ${r.to}`);
          }
        } catch {
          if (++misses >= 5) {
            clearInterval(timer.current);
            setBusy(false);
            toast.error("Lost contact with the test run — refresh the page to see its results");
          }
        }
      }, 1500);
    } catch (e) {
      setBusy(false);
      toast.error(e.response?.data?.detail || "Couldn't start the test");
    }
  };

  if (!catalogue) return null;
  const groups = Object.entries(catalogue.groups);
  const resultFor = (k) => run?.results?.find((r) => r.key === k);

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4" data-testid="email-test-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-semibold">Send test e-mails</h2>
          <p className="text-xs text-muted-foreground">One real sample of each ticked e-mail goes through the live mail server to the address below (sample content, no real customer data).</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" className="h-8 w-80 text-sm" data-testid="email-test-to" />
          <Button size="sm" onClick={start} disabled={busy || keys.size === 0 || !to.includes("@")} data-testid="email-test-send">
            {busy ? <CircleNotch size={14} className="mr-1 animate-spin" /> : <PaperPlaneTilt size={14} className="mr-1" />}{busy ? "Sending…" : `Send ${keys.size} sample${keys.size === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {groups.map(([g, label]) => {
          const items = catalogue.samples.filter((s) => s.group === g);
          const allOn = items.every((s) => keys.has(s.key));
          return (
            <div key={g} className="rounded-md border border-border p-3" data-testid={`email-test-group-${g}`}>
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 cursor-pointer">
                <Checkbox checked={allOn} onCheckedChange={(v) => toggleGroup(g, !!v)} data-testid={`email-test-group-check-${g}`} /> {label}
              </label>
              <div className="space-y-1.5">
                {items.map((s) => {
                  const r = resultFor(s.key);
                  return (
                    <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`email-test-sample-${s.key}`}>
                      <Checkbox checked={keys.has(s.key)} onCheckedChange={() => toggle(s.key)} data-testid={`email-test-check-${s.key}`} />
                      <span className="flex-1">{s.label}</span>
                      {r && r.status === "sent" && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]" data-testid={`email-test-result-${s.key}`}><CheckCircle size={11} className="mr-1" /> sent · {r.provider}{r.ms ? ` · ${(r.ms / 1000).toFixed(1)}s` : ""}</Badge>}
                      {r && r.status === "failed" && <Badge className="bg-red-100 text-red-700 text-[10px] max-w-[260px] truncate" title={r.error} data-testid={`email-test-result-${s.key}`}><XCircle size={11} className="mr-1" /> {r.error || "failed"}</Badge>}
                      {r && r.status === "queued" && run?.status === "running" && <Badge variant="outline" className="text-[10px]" data-testid={`email-test-result-${s.key}`}>queued</Badge>}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {run && run.status !== "running" && (
        <div className={`rounded-md px-3 py-2 text-xs ${run.failed ? "bg-red-50 dark:bg-red-950/20 text-red-800 dark:text-red-200" : "bg-emerald-50 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200"}`} data-testid="email-test-summary">
          {run.failed ? `${run.failed} failed, ${run.sent} sent` : `All ${run.sent} accepted by the mail server`} · to {run.to} · finished {new Date(run.finished_at).toLocaleTimeString("en-GB")}
        </div>
      )}
    </div>
  );
}
